import { useEffect, useState } from 'react';

// Shared in-memory event system
const listeners = new Map<string, Set<(value: any) => void>>();

function emitChange<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
  // biome-ignore lint/complexity/noForEach: <explanation>
  listeners.get(key)?.forEach((cb) => cb(value));
}

export function useLocalStorageWatch<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    // Add this hook’s setValue as a listener
    const cb = (val: T) => setValue(val);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)?.add(cb);

    // Sync with external tab changes
    const onStorage = (event: StorageEvent) => {
      if (event.key === key && event.newValue) {
        try {
          const newVal = JSON.parse(event.newValue) as T;
          setValue(newVal);
        } catch {}
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      listeners.get(key)?.delete(cb);
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  const updateValue = (newVal: T) => emitChange(key, newVal);

  return [value, updateValue];
}

export default useLocalStorageWatch;
